import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import clientService, { ClientDetail as ClientDetailType, ClientStats } from '../../services/clientService';
import { useClient } from '../../contexts/ClientContext';
import { useAuth } from '../../contexts/AuthContext';
import { useUserTier } from '../../hooks/useUserTier';
import { InviteUserModal } from '../team/InviteUserModal';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { getApiUrl } from '../../utils/config';
import { motion } from 'framer-motion';
import { ArrowLeft, Edit, Trash2, Users, BarChart2, FileText, Image, Zap, Globe, Mail, Phone, Building, Star, Calendar, UserPlus, MoreVertical, X, Paintbrush, Crown } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="relative bg-white/70 dark:bg-slate-800/50 backdrop-blur-sm p-6 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 flex items-center space-x-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${color}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div>
      <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">{label}</p>
      <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
    </div>
  </div>
);

const ClientDetail: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canEdit, canDelete, canManageUsers, setCurrentClient } = useClient();
  const { currentTier } = useUserTier();
  
  const [client, setClient] = useState<ClientDetailType | null>(null);
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [teamLimits, setTeamLimits] = useState<any>(null);

  useEffect(() => {
    if (clientId) {
      loadClient();
      loadStats();
      loadTeamLimits();
    }
  }, [clientId]);

  const loadTeamLimits = async () => {
    if (!clientId) return;
    try {
      const url = `${getApiUrl()}/api/v1/clients/${clientId}/team/limits`;
      console.log('Fetching team limits from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      
      console.log('Team limits response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Team limits loaded:', data);
        setTeamLimits(data);
      } else {
        console.error('Team limits API error:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Failed to load team limits:', error);
    }
  };

  const loadClient = async () => {
    if (!clientId) return;
    try {
      setIsLoading(true);
      setError(null);
      const clientData = await clientService.getClient(clientId);
      setClient(clientData);
      setCurrentClient(clientData);
    } catch (err: any) {
      setError('Failed to load brand details');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    if (!clientId) return;
    try {
      setIsLoadingStats(true);
      const statsData = await clientService.getClientStats(clientId);
      setStats(statsData);
    } catch (err: any) {
      // Silently fail for stats
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleDelete = async () => {
    if (!clientId) return;
    try {
      setIsDeleting(true);
      await clientService.deleteClient(clientId);
      navigate('/app/clients');
    } catch (err: any) {
      setError('Failed to delete brand');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const baseClasses = 'inline-flex items-center px-3 py-1 rounded-full text-xs font-bold capitalize';
    switch (role) {
      case 'owner': return `${baseClasses} bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20`;
      case 'admin': return `${baseClasses} bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20`;
      case 'editor': return `${baseClasses} bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20`;
      default: return `${baseClasses} bg-slate-500/10 text-slate-600 dark:text-slate-400 border border-slate-500/20`;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="w-16 h-16 border-4 border-slate-200/50 dark:border-slate-600/30 border-t-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-slate-900">
        <p className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Error</p>
        <p className="text-slate-600 dark:text-slate-400 mb-6">{error || 'Brand not found'}</p>
        <Link to="/app/clients">
          <motion.button 
            className="flex items-center space-x-2 px-5 py-3 bg-red-500/20 text-red-600 dark:text-red-300 font-bold rounded-xl border border-red-500/30 hover:bg-red-500/30 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to My Brand</span>
          </motion.button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* Floating Header */}
      <div className="backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/50 dark:border-slate-700/30">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="flex items-center space-x-4">
                <Link to="/app/clients">
                  <motion.button whileHover={{ scale: 1.1, x: -2 }} className="p-2 rounded-full bg-white/70 dark:bg-slate-800/50 hover:bg-slate-100/70 dark:hover:bg-slate-700/70 border border-slate-300/50 dark:border-slate-600/50">
                    <ArrowLeft className="w-6 h-6 text-slate-600 dark:text-slate-300" />
                  </motion.button>
                </Link>
                <div className="w-16 h-16 bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 rounded-2xl flex items-center justify-center border border-slate-300/50 dark:border-slate-600/50">
                  {client.logo_url ? (
                    <img src={client.logo_url} alt={client.name} className="w-full h-full rounded-2xl object-cover" />
                  ) : (
                    <span className="text-3xl font-black text-indigo-600 dark:text-indigo-400">{client.name.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <h1 className="text-4xl font-black bg-gradient-to-r from-gray-900 via-indigo-500 to-purple-500 bg-clip-text text-transparent dark:from-white dark:via-indigo-200 dark:to-purple-200">
                    {client.name}
                  </h1>
                  <p className="text-slate-600 dark:text-slate-300 text-lg">@{client.slug}</p>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="flex items-center space-x-3">
              {canEdit() && (
                <>
                  <Link to={`/app/clients/${client.id}/brand-kit`}>
                    <motion.button 
                      className="flex items-center space-x-2 px-5 py-3 bg-purple-500/20 text-purple-600 dark:text-purple-300 font-bold rounded-xl border border-purple-500/30 hover:bg-purple-500/30 transition-all duration-300"
                      whileHover={{ scale: 1.05, y: -2 }}
                    >
                      <Paintbrush className="w-5 h-5" />
                      <span>Brand Kit</span>
                    </motion.button>
                  </Link>
                  <Link to={`/app/clients/${client.id}/edit`}>
                    <motion.button 
                      className="flex items-center space-x-2 px-5 py-3 bg-slate-200/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 font-bold rounded-xl border border-slate-300/50 dark:border-slate-600/50 hover:bg-slate-300/70 dark:hover:bg-slate-700 transition-all duration-300"
                      whileHover={{ scale: 1.05, y: -2 }}
                    >
                      <Edit className="w-5 h-5" />
                      <span>Edit</span>
                    </motion.button>
                  </Link>
                </>
              )}
              {canDelete() && !['starter', 'creator'].includes(client.subscription_tier) && (
                <motion.button 
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center space-x-2 px-5 py-3 bg-red-500/20 text-red-600 dark:text-red-300 font-bold rounded-xl border border-red-500/30 hover:bg-red-500/30 transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                >
                  <Trash2 className="w-5 h-5" />
                  <span>Delete</span>
                </motion.button>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <StatCard icon={Users} label="Users" value={stats?.total_users ?? 'N/A'} color="from-emerald-500 to-teal-600" />
              <StatCard icon={FileText} label="Posts" value={stats?.total_posts ?? 'N/A'} color="from-blue-500 to-sky-600" />
              <StatCard icon={Image} label="Media" value={stats?.total_media_files ?? 'N/A'} color="from-amber-500 to-orange-600" />
              <StatCard icon={Zap} label="AI Tasks" value={stats?.total_ai_tasks ?? 'N/A'} color="from-rose-500 to-red-600" />
            </div>
          </motion.div>

          {/* Team Members */}
          {canManageUsers() && (
            <motion.div 
              className="relative bg-white/70 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-3xl p-8"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center"><Users className="w-6 h-6 mr-3 text-indigo-600 dark:text-indigo-400"/>Team Members</h2>
                <motion.button 
                  onClick={() => {
                    console.log('Team limits:', teamLimits);
                    // Check if team member limit is reached
                    if (teamLimits && !teamLimits.allowed && teamLimits.limit_value !== -1) {
                      console.log('Showing upgrade modal');
                      setShowUpgradeModal(true);
                    } else {
                      console.log('Showing invite modal');
                      setShowInviteModal(true);
                    }
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-indigo-500/20 text-indigo-600 dark:text-indigo-300 font-bold rounded-xl border border-indigo-500/30 hover:bg-indigo-500/30 hover:text-indigo-700 dark:hover:text-white transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                >
                  <UserPlus className="w-5 h-5" />
                  <span>Invite User</span>
                </motion.button>
              </div>
              <div className="space-y-4">
                {client.users.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-4 bg-slate-100/70 dark:bg-slate-700/50 rounded-2xl border border-slate-200/50 dark:border-slate-600/50">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-slate-300 to-slate-400 dark:from-slate-600 dark:to-slate-700 rounded-full flex items-center justify-center border border-slate-300/50 dark:border-slate-500/50">
                        <span className="font-bold text-slate-700 dark:text-slate-300">{user.user_username?.charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 dark:text-slate-200">{user.user_username}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{user.user_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className={getRoleBadge(user.role)}>{user.role}</div>
                      {user.role !== 'owner' && (
                        <button className="p-2 rounded-full hover:bg-slate-200/50 dark:hover:bg-slate-600/50 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white">
                          <MoreVertical className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Right Column */}
        <div className="lg:col-span-1 space-y-8">
          <motion.div 
            className="bg-white/70 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-3xl p-8"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center"><Building className="w-6 h-6 mr-3 text-indigo-600 dark:text-indigo-400"/>Brand Details</h2>
            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center"><Globe className="w-4 h-4 mr-2"/>Website</h3>
                {client.website ? <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">{client.website}</a> : <p className="text-slate-700 dark:text-slate-300">Not provided</p>}
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center"><Mail className="w-4 h-4 mr-2"/>Email</h3>
                {client.email ? <a href={`mailto:${client.email}`} className="text-indigo-600 dark:text-indigo-400 hover:underline">{client.email}</a> : <p className="text-slate-700 dark:text-slate-300">Not provided</p>}
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center"><Phone className="w-4 h-4 mr-2"/>Phone</h3>
                <p className="text-slate-700 dark:text-slate-300">{client.phone || 'Not provided'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center"><Calendar className="w-4 h-4 mr-2"/>Created On</h3>
                <p className="text-slate-700 dark:text-slate-300">{new Date(client.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-200/50 dark:border-slate-700/50">
              <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Description</h3>
              <p className="text-slate-700 dark:text-slate-300 text-sm italic">{client.description || 'No description provided'}</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50">
          <motion.div 
            className="bg-white/90 dark:bg-slate-800/80 border border-red-500/30 rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl shadow-red-500/10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <h3 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4 flex items-center"><Trash2 className="w-6 h-6 mr-3"/>Delete Brand</h3>
            <p className="text-slate-700 dark:text-slate-300 mb-6">
              Are you sure you want to permanently delete <span className="font-bold text-gray-900 dark:text-white">{client.name}</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <motion.button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-6 py-3 bg-slate-200/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 font-bold rounded-xl border border-slate-300/50 dark:border-slate-600/50 hover:bg-slate-300/70 dark:hover:bg-slate-700 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-6 py-3 bg-red-500/20 text-red-600 dark:text-red-300 font-bold rounded-xl border border-red-500/30 hover:bg-red-500/30 transition-all duration-300 disabled:opacity-50"
                whileHover={{ scale: 1.05 }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Team Member Limit Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              <span>Team Member Limit Reached</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                You've reached your team member limit of {teamLimits?.limit_value} member{teamLimits?.limit_value !== 1 ? 's' : ''}.
              </p>
            </div>
            
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Crown className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 dark:text-white mb-1">Upgrade to Add More Team Members</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    Upgrade to a higher tier plan to expand your team limits and unlock advanced collaboration features.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUpgradeModal(false)}
              className="w-full sm:w-auto"
            >
              Maybe Later
            </Button>
            <Button
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/pricing');
              }}
              className="w-full sm:w-auto text-white"
              style={{ 
                background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                backgroundSize: '200% 200%',
                backgroundPosition: 'left center',
                boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundPosition = 'right center';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(48, 179, 221, 0.4)';
                e.currentTarget.style.transform = 'translateY(-1px) scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundPosition = 'left center';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(48, 179, 221, 0.3)';
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
              }}
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        clientId={client?.id}
        onSuccess={() => {
          setShowInviteModal(false);
          // Refresh client data to show new team member
          loadClient();
        }}
      />
    </div>
  );
};

export default ClientDetail;
